import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  HStack,
  List,
  Portal,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useLocalStorage, useLogout } from 'hooks';
import { useTranslation } from 'localization';
import { routes } from 'statics';

import {
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from '../ServiceDialog';

type MenuListProps = {
  list: Array<{ title: string; link: string }>;
  closeDrawer?: () => void;
};

const MenuList: React.FC<MenuListProps> = ({ list, closeDrawer }) => {
  const itemPx = closeDrawer ? 3 : 4;
  const { t } = useTranslation('layout.header');
  const { setItem } = useLocalStorage();
  const navigate = useNavigate();

  const { open, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef<any>();

  const { mutateAsync: logout } = useLogout();

  return (
    <>
      <VStack align="stretch" flex="1" minH={0} w="100%" gap={0}>
        <List.Root
          as="ul"
          listStyleType="none"
          display="flex"
          flexDir="column"
          gap={0}
          margin={0}
          p={0}
        >
          {list.map((item) => (
            <NavLink
              key={item.link}
              to={item.link}
              onClick={closeDrawer ? () => closeDrawer() : undefined}
            >
              {({ isActive }) => (
                <List.Item
                  textTransform="capitalize"
                  fontSize="var(--chakra-font-sizes-sm)"
                  fontWeight={isActive ? 'medium' : 'normal'}
                  borderRadius="lg"
                  bg={isActive ? 'dark' : 'transparent'}
                  color="body"
                  mb="6px"
                  py={3}
                  px={itemPx}
                  transition="background 0.15s ease, color 0.15s ease"
                  cursor="pointer"
                  _hover={{
                    bg: isActive ? 'dark' : 'rgba(255, 255, 255, 0.06)',
                    color: 'body',
                  }}
                >
                  {t(item.title)}
                </List.Item>
              )}
            </NavLink>
          ))}
        </List.Root>
        <Box
          mt="auto"
          flexShrink={0}
          pt={2}
          fontSize="md"
          fontWeight="medium"
          py={3}
          px={itemPx}
          color="error"
          borderRadius="lg"
          cursor="pointer"
          transition="background 0.15s ease"
          _hover={{ bg: 'rgba(255, 255, 255, 0.06)' }}
          onClick={() => onOpen()}
        >
          {t('logout')}
        </Box>
      </VStack>

      <Dialog.Root
        motionPreset="slide-in-bottom"
        initialFocusEl={() => cancelRef.current}
        open={open}
        placement="center"
        closeOnInteractOutside={false}
        role="alertdialog"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            onClose();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>{t('logoutTitle')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <ServiceDialogBody>
                <Text fontSize="sm" color="body2">
                  {t('logoutDescription')}
                </Text>
              </ServiceDialogBody>
              <ServiceDialogFooter>
                <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                  <Button
                    ref={cancelRef}
                    variant="ghost"
                    size="sm"
                    h={10}
                    onClick={onClose}
                  >
                    {t('no')}
                  </Button>
                  <Button
                    colorPalette="red"
                    size="sm"
                    h={10}
                    onClick={() =>
                      logout([]).then(() => {
                        setItem('magix-token', '');
                        onClose();
                        navigate(routes.auth.signIn.url);
                      })
                    }
                  >
                    {t('yes')}
                  </Button>
                </HStack>
              </ServiceDialogFooter>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  );
};

export default MenuList;
